/**
 * <s-product-image-zoom> — PDP gallery navigation and zoom.
 *
 * Gallery image buttons open the shared zoom dialog declaratively via
 * commandfor/show-modal. This component:
 *   - steps the main gallery track with prev/next arrows (`--previous`, `--next`)
 *     and desktop thumbnails (`--show` + data-gallery-index), looping at the ends;
 *   - keeps thumbnail aria-current in sync with the visible slide;
 *   - opens the zoom dialog on the clicked image and steps it with its own arrows
 *     (`--zoom-previous`, `--zoom-next`) or the left/right arrow keys;
 *   - warms the selected zoom image on hover/focus.
 *
 * Required markup:
 *   [data-zoom-dialog]                         — on the host; id of the zoom dialog.
 *   [data-gallery-track] > [data-gallery-slide] — main scroll-snap track and slides.
 *   [data-gallery-thumb][data-gallery-index]   — optional thumbnail buttons.
 *   [data-zoom-preload][data-zoom-index]       — gallery image buttons that open the dialog.
 *   dialog [data-zoom-track] > [data-zoom-slide] img[data-zoom-image] — zoom slides.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #pendingZoomIndex = 0;
  #track;
  #dialog;
  #zoomTrack;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    const dialogId = this.dataset.zoomDialog;
    this.#dialog = dialogId ? document.getElementById(dialogId) : null;
    this.#zoomTrack = this.#dialog?.querySelector('[data-zoom-track]') ?? null;

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('command', this.#handleCommand, opts);

    this.#track?.addEventListener('scroll', this.#syncThumbs, { ...opts, passive: true });

    if (this.#dialog) {
      // CommandEvent does not bubble; capture the opening source on the dialog itself.
      this.#dialog.addEventListener('command', (event) => {
        if (event.command !== 'show-modal') return;
        const index = Number(event.source?.dataset?.zoomIndex);
        this.#pendingZoomIndex = Number.isFinite(index) ? index : 0;
      }, opts);

      this.#dialog.addEventListener('toggle', (event) => {
        if (event.newState !== 'open') return;
        this.#scrollToIndex(this.#zoomTrack, this.#pendingZoomIndex, 'instant');
      }, opts);

      this.#dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const forward = event.key === 'ArrowRight' ? 1 : -1;
        this.#step(this.#zoomTrack, this.#isRtl() ? -forward : forward);
      }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handleCommand = (event) => {
    switch (event.command) {
      case '--previous':
        this.#step(this.#track, -1);
        break;
      case '--next':
        this.#step(this.#track, 1);
        break;
      case '--show':
        this.#scrollToIndex(this.#track, Number(event.source?.dataset?.galleryIndex) || 0, 'smooth');
        break;
      case '--zoom-previous':
        this.#step(this.#zoomTrack, -1);
        break;
      case '--zoom-next':
        this.#step(this.#zoomTrack, 1);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  #slideCount(track) {
    return track ? track.children.length : 0;
  }

  #currentIndex(track) {
    if (!track || track.clientWidth === 0) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #step(track, direction) {
    const count = this.#slideCount(track);
    if (count < 2) return;
    const next = (this.#currentIndex(track) + direction + count) % count;
    this.#scrollToIndex(track, next, 'smooth');
  }

  #scrollToIndex(track, index, behavior) {
    if (!track) return;
    const count = this.#slideCount(track);
    const clamped = Math.max(0, Math.min(index, count - 1));
    const sign = this.#isRtl() ? -1 : 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: sign * clamped * track.clientWidth,
      behavior: reduceMotion ? 'instant' : behavior,
    });
    if (track === this.#track) this.#setCurrentThumb(clamped);
  }

  #syncThumbs = () => {
    this.#setCurrentThumb(this.#currentIndex(this.#track));
  };

  #setCurrentThumb(index) {
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      if (Number(thumb.dataset.galleryIndex) === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(Number(source.dataset.zoomIndex) || 0);
  };

  #preload(index) {
    if (this.#preloaded.has(index)) return;

    const slide = this.#zoomTrack?.children[index];
    const image = slide?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(index);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
